/**
 * Checks if a stream URL is ready for a given task ID and conversion ID
 * Calls MusicGPT API directly to check stream availability
 * Based on the sample code provided
 * @param taskId - MusicGPT task ID
 * @param conversionId - Conversion ID (conversion_id_1 or conversion_id_2)
 * @returns Promise with stream readiness status and URL if ready
 */
export async function checkStreamReady(
  taskId: string,
  conversionId: string,
): Promise<{ ready: boolean; url?: string; timestamp?: Date }> {
  const streamUrl = `https://api.musicgpt.com/api/public/v2/MusicAI/stream?task_id=${encodeURIComponent(
    taskId,
  )}&conversion_id=${encodeURIComponent(conversionId)}`;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const response = await fetch(streamUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle 422 - stream not ready yet
    if (response.status === 422) {
      console.log('[Stream Ready] ⏳ Stream not ready yet (422 status)');
      return { ready: false, timestamp: new Date() };
    }

    if (response.status === 200) {
      const contentType = response.headers.get('content-type') || '';
      if (
        contentType.includes('audio/') ||
        contentType.includes('application/octet-stream')
      ) {
        if (import.meta.env.DEV) {
          console.log(
            '[Stream Ready] ✅ Stream is ready! (Content-Type indicates audio)',
          );
        }
        return { ready: true, timestamp: new Date(), url: streamUrl };
      }

      try {
        const reader = response.body?.getReader();
        if (!reader) {
          return { ready: false, timestamp: new Date() };
        }

        const { done, value } = await reader.read();

        reader.cancel().catch(() => {});

        if (done) {
          return { ready: false, timestamp: new Date() };
        }

        const chunkToCheck = value.slice(0, Math.min(value.length, 1000));

        const decoder = new TextDecoder('utf-8', { fatal: false });
        const chunkText = decoder.decode(chunkToCheck);

        if (
          chunkText.includes('"detail":"Audio Stream Not Yet Available"') ||
          chunkText.includes('Audio Stream Not Yet Available')
        ) {
          return { ready: false, timestamp: new Date() };
        }

        if (import.meta.env.DEV) {
          console.log(
            '[Stream Ready] ✅ Stream is ready! (Response is not the error message)',
          );
        }
        return { ready: true, timestamp: new Date(), url: streamUrl };
      } catch {
        if (import.meta.env.DEV) {
          console.log(
            '[Stream Ready] ✅ Stream is ready! (Failed to decode as text, likely audio data)',
          );
        }
        return { ready: true, timestamp: new Date(), url: streamUrl };
      }
    }

    console.log(`[Stream Ready] ❌ Unexpected status: ${response.status}`);

    return { ready: false, timestamp: new Date() };
  } catch (error: any) {
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      console.log('[Stream Ready] ⏱️ Request timed out (3s)');
      return { ready: false, timestamp: new Date() };
    }

    if (
      error.message &&
      (error.message.includes('aborted') ||
        error.code === 'ECONNABORTED' ||
        error.name === 'AbortError')
    ) {
      return { ready: false, timestamp: new Date() };
    }

    if (import.meta.env.DEV) {
      console.error('[Stream Ready Check] Error:', error);
      // failed to fetch from musicgpt, v1 didnt had this issue, but as per their team we have to use v2
      if (error.message?.includes('Failed to fetch')) {
        console.log(
          '[Stream Ready Check] Unable to fetch from musicgpt',
        );
      }
    }
    return { ready: false, timestamp: new Date() };
  }
}

/**
 * Waits for a stream URL to be ready
 * Based on the reference implementation provided
 */
export async function waitForStreamReady(
  taskId: string,
  conversionId: string,
  options: {
    initialDelay?: number;
    maxAttempts?: number;
    interval?: number;
    exponentialBackoff?: boolean;
    backoffMultiplier?: number;
  } = {},
): Promise<{ url: string; readyAt: Date; startTime: Date; elapsed: number }> {
  const {
    initialDelay = 2000,
    maxAttempts = 60,
    interval = 3000,
    exponentialBackoff = true,
    backoffMultiplier = 1.2,
  } = options;

  const startTime = new Date();
  let currentInterval = interval;
  let attempts = 0;

  if (initialDelay > 0) {
    if (import.meta.env.DEV) {
      console.log(
        `[Stream Ready] Waiting ${initialDelay}ms before first check...`,
      );
    }
    await new Promise((resolve) => setTimeout(resolve, initialDelay));
  }

  if (import.meta.env.DEV) {
    console.log(`[Stream Ready] Starting to poll stream URL...`);
    console.log(`[Stream Ready] Task ID: ${taskId}`);
    console.log(`[Stream Ready] Conversion ID: ${conversionId}`);
  }

  while (attempts < maxAttempts) {
    attempts++;
    const checkTime = new Date();

    if (import.meta.env.DEV) {
      console.log(
        `[Stream Ready] [Attempt ${attempts}/${maxAttempts}] Checking stream at ${checkTime.toISOString()}...`,
      );
    }

    const result = await checkStreamReady(taskId, conversionId);

    if (result.ready && result.url) {
      const readyAt = result.timestamp || new Date();
      const elapsed = readyAt.getTime() - startTime.getTime();

      if (import.meta.env.DEV) {
        console.log('\n✅ Stream is ready!');
        console.log(`Ready at: ${readyAt.toISOString()}`);
        console.log(
          `⏱️  TOTAL TIME TAKEN: ${(elapsed / 1000).toFixed(2)} seconds (${(
            elapsed /
            1000 /
            60
          ).toFixed(2)} minutes)`,
        );
        console.log(`Stream URL: ${result.url}`);
      }

      return {
        url: result.url,
        readyAt: readyAt,
        startTime: startTime,
        elapsed: elapsed,
      };
    }

    if (attempts < maxAttempts) {
      const waitTime = exponentialBackoff ? currentInterval : interval;
      if (import.meta.env.DEV) {
        console.log(
          `Not ready yet. Waiting ${waitTime}ms before next check...`,
        );
      }
      await new Promise((resolve) => setTimeout(resolve, waitTime));

      if (exponentialBackoff) {
        currentInterval = Math.min(currentInterval * backoffMultiplier, 30000);
      }
    }
  }

  throw new Error(
    `Stream not ready after ${attempts} attempts (${(
      (new Date().getTime() - startTime.getTime()) /
      1000
    ).toFixed(2)} seconds)`,
  );
}

/**
 * Checks both polling status and stream URL, returns whichever is ready first
 * @param musicGptTaskId - MusicGPT task ID (for stream checking)
 * @param conversionId - Conversion ID for the song (conversion_id_1 or conversion_id_2)
 * @param getPollingStatus - Function to get polling status
 * @param songVersion - Song version (1 or 2) for polling fallback
 * @returns Promise with song URL from whichever source is ready first
 */
export async function checkSongReadyDual(
  musicGptTaskId: string | undefined,
  conversionId: string | undefined,
  getPollingStatus: () => Promise<{
    songUrl1?: string;
    songUrl2?: string;
  }>,
  songVersion: 1 | 2,
): Promise<{ url: string; source: 'polling' | 'stream' } | null> {
  if (!musicGptTaskId || !conversionId) {
    const pollingStatus = await getPollingStatus();
    const url =
      songVersion === 1 ? pollingStatus.songUrl1 : pollingStatus.songUrl2;
    return url ? { url, source: 'polling' as const } : null;
  }

  const [pollingResult, streamResult] = await Promise.allSettled([
    getPollingStatus(),
    checkStreamReady(musicGptTaskId, conversionId),
  ]);

  const pollingStatus =
    pollingResult.status === 'fulfilled' ? pollingResult.value : null;
  const streamStatus =
    streamResult.status === 'fulfilled' ? streamResult.value : null;

  const pollingUrl =
    songVersion === 1 ? pollingStatus?.songUrl1 : pollingStatus?.songUrl2;

  if (streamStatus?.ready && streamStatus.url) {
    return { url: streamStatus.url, source: 'stream' };
  }

  if (pollingUrl) {
    return { url: pollingUrl, source: 'polling' };
  }

  return null;
}
